import { create } from "zustand";
import { jwtDecode } from "jwt-decode";

const authStore = (set, get) => ({
  // User state
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,

  // Set tokens and decode user info
  setTokens: (accessToken, refreshToken) => {
    try {
      // Decode token to extract user info
      const decoded = jwtDecode(accessToken);
      
      // Extract user info from token claims
      const userInfo = {
        id: decoded.sub || decoded.id || decoded.userId || decoded.nameid,
        email: decoded.email || decoded.Email || decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
        firstName: decoded.firstname || decoded.firstName || decoded.FirstName || '',
        lastName: decoded.lastname || decoded.lastName || decoded.LastName || '',
        image: decoded.image || decoded.Image || '',
        phone: decoded.phone || decoded.PhoneNumber || decoded.phoneNumber || '',
        address: decoded.address || decoded.Address || '',
        addressLine1: decoded.addressLine1 || decoded.AddressLine1 || '',
        addressLine2: decoded.addressLine2 || decoded.AddressLine2 || '',
        city: decoded.city || decoded.City || '',
        state: decoded.state || decoded.State || '',
        zipCode: decoded.zipCode || decoded.ZipCode || '',
        country: decoded.country || decoded.Country || '',
        // Keep name for backward compatibility (firstname only for display)
        name: decoded.firstname || decoded.firstName || decoded.FirstName || 'User',
        // Include all decoded claims
        ...decoded
      };

      // Store tokens in localStorage
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      set({
        accessToken,
        refreshToken,
        user: userInfo,
        isAuthenticated: true,
      });
    } catch (error) {
      console.error('Error decoding token:', error);
      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        isAuthenticated: false,
      });
    }
  },

  // Initialize auth state from localStorage
  initializeAuth: () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;

        // Check if token is expired
        if (decoded.exp && decoded.exp < currentTime) {
          // Token expired, clear it
          get().logout();
          return;
        }

        // Token is valid, set user state
        const userInfo = {
          id: decoded.sub || decoded.id || decoded.userId || decoded.nameid,
          email: decoded.email || decoded.Email || decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
          firstName: decoded.firstname || decoded.firstName || decoded.FirstName || '',
          lastName: decoded.lastname || decoded.lastName || decoded.LastName || '',
          image: decoded.image || decoded.Image || '',
          phone: decoded.phone || decoded.PhoneNumber || decoded.phoneNumber || '',
          address: decoded.address || decoded.Address || '',
          addressLine1: decoded.addressLine1 || decoded.AddressLine1 || '',
          addressLine2: decoded.addressLine2 || decoded.AddressLine2 || '',
          city: decoded.city || decoded.City || '',
          state: decoded.state || decoded.State || '',
          zipCode: decoded.zipCode || decoded.ZipCode || '',
          country: decoded.country || decoded.Country || '',
          // Keep name for backward compatibility (firstname only for display)
          name: decoded.firstname || decoded.firstName || decoded.FirstName || 'User',
          // Include all decoded claims
          ...decoded
        };

        set({
          accessToken,
          refreshToken,
          user: userInfo,
          isAuthenticated: true,
        });
      } catch (error) {
        console.error('Error initializing auth:', error);
        get().logout();
      }
    }
  },

  // Update user info
  setUser: (userInfo) => {
    set({ user: userInfo });
  },

  // Logout - clear all auth data
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
    });
  },

  // Check if token is expired
  isTokenExpired: () => {
    const { accessToken } = get();
    if (!accessToken) return true;

    try {
      const decoded = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;
      return decoded.exp && decoded.exp < currentTime;
    } catch (error) {
      return true;
    }
  },
});

const useAuthStore = create(authStore);

export default useAuthStore;

